import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dumbbell, RefreshCw } from 'lucide-react';

export default function BarbellPlateCalculator() {
  const [unit, setUnit] = useState<'LBS' | 'KGS'>('LBS');
  const [targetWeight, setTargetWeight] = useState<number>(135);
  const [barWeight, setBarWeight] = useState<number>(45);
  const [plates, setPlates] = useState<{ weight: number; count: number }[]>([]);

  const AVAILABLE_PLATES = {
    LBS: [45, 35, 25, 10, 5, 2.5],
    KGS: [25, 20, 15, 10, 5, 2.5, 1.25]
  };

  const calculatePlates = (target: number, bar: number, currentUnit: 'LBS' | 'KGS') => {
    if (target <= bar) {
      setPlates([]);
      return;
    }

    let remainingWeightPerSide = (target - bar) / 2;
    const result: { weight: number; count: number }[] = [];

    AVAILABLE_PLATES[currentUnit].forEach(plate => {
      const count = Math.floor(remainingWeightPerSide / plate);
      if (count > 0) {
        result.push({ weight: plate, count });
        remainingWeightPerSide -= count * plate;
      }
    });

    setPlates(result);
  };

  useEffect(() => {
    calculatePlates(targetWeight, barWeight, unit);
  }, [targetWeight, barWeight, unit]);

  const handleUnitToggle = (newUnit: 'LBS' | 'KGS') => {
    setUnit(newUnit);
    if (newUnit === 'KGS') {
      setTargetWeight(60); // standard starting metric weight (20kg bar + 20kg plates)
      setBarWeight(20);
    } else {
      setTargetWeight(135); // standard starting imperial weight (45lb bar + 45lb plates)
      setBarWeight(45);
    }
  };

  const handleQuickWeight = (weight: number) => {
    setTargetWeight(weight);
  };

  const quickWeights = {
    LBS: [95, 135, 185, 225, 275, 315],
    KGS: [40, 60, 80, 100, 120, 140]
  };

  return (
    <div className="bg-secondary/40 border border-border/60 rounded-sm p-4 space-y-4">
      <div className="flex items-center justify-between border-b border-border/40 pb-2">
        <span className="text-xs font-mono text-primary uppercase tracking-wider">Barbell Plate Loading</span>
        <div className="flex bg-background/60 p-0.5 rounded-sm border border-border/40">
          <button
            onClick={() => handleUnitToggle('LBS')}
            className={`px-2 py-0.5 text-[9px] font-mono rounded-sm transition-all ${unit === 'LBS' ? 'bg-primary text-primary-foreground font-bold' : 'text-muted-foreground'}`}
          >
            LBS
          </button>
          <button
            onClick={() => handleUnitToggle('KGS')}
            className={`px-2 py-0.5 text-[9px] font-mono rounded-sm transition-all ${unit === 'KGS' ? 'bg-primary text-primary-foreground font-bold' : 'text-muted-foreground'}`}
          >
            KGS
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Target Weight ({unit})</label>
          <Input
            type="number"
            value={targetWeight || ''}
            onChange={(e) => setTargetWeight(parseFloat(e.target.value) || 0)}
            className="h-8 bg-background border-border font-mono text-xs text-center"
          />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Bar Weight ({unit})</label>
          {unit === 'LBS' ? (
            <select
              value={barWeight}
              onChange={(e) => setBarWeight(parseInt(e.target.value))}
              className="w-full h-8 rounded-sm border border-border bg-background text-foreground px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="45">45 LBS (Standard)</option>
              <option value="35">35 LBS (Light)</option>
              <option value="55">55 LBS (Heavy/Squat)</option>
              <option value="0">0 LBS (No Bar)</option>
            </select>
          ) : (
            <select
              value={barWeight}
              onChange={(e) => setBarWeight(parseInt(e.target.value))}
              className="w-full h-8 rounded-sm border border-border bg-background text-foreground px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="20">20 KGS (Standard)</option>
              <option value="15">15 KGS (Light)</option>
              <option value="25">25 KGS (Heavy/Squat)</option>
              <option value="0">0 KGS (No Bar)</option>
            </select>
          )}
        </div>
      </div>

      {/* Quick Select Buttons */}
      <div className="space-y-1">
        <span className="block text-[9px] font-mono uppercase text-muted-foreground">Quick Targets</span>
        <div className="flex flex-wrap gap-1">
          {quickWeights[unit].map(w => (
            <Button
              key={w}
              variant="outline"
              size="sm"
              onClick={() => handleQuickWeight(w)}
              className={`h-6 px-2 text-[10px] font-mono ${targetWeight === w ? 'border-primary text-primary bg-primary/10' : 'border-border text-muted-foreground'}`}
            >
              {w} {unit}
            </Button>
          ))}
        </div>
      </div>

      {/* Plates Display Visualization */}
      <div className="bg-background/40 border border-border/40 rounded-sm p-3 text-center space-y-3">
        <span className="block text-[9px] font-mono uppercase text-muted-foreground">Plates per side</span>
        
        {plates.length === 0 ? (
          <div className="text-xs font-mono text-muted-foreground uppercase py-2">
            {targetWeight <= barWeight ? "WEIGHT MATCHES OR UNDER BAR WEIGHT" : "NO PLATES REQUIRED"}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5 max-h-[120px] overflow-y-auto">
            {plates.map(p => (
              <div key={p.weight} className="flex items-center justify-between bg-secondary/30 px-3 py-1.5 rounded-sm border border-border/30 text-xs">
                <span className="font-mono font-bold text-primary">{p.weight} {unit} plate</span>
                <span className="font-mono bg-primary/20 border border-primary/30 text-primary px-2 py-0.5 rounded-sm text-[10px] font-bold">
                  x {p.count}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Visual Barbell Representation */}
        {plates.length > 0 && (
          <div className="flex items-center justify-center gap-1 pt-2 border-t border-border/20">
            {/* Left side plates */}
            <div className="flex items-center gap-0.5 flex-row-reverse">
              {plates.map(p => 
                Array.from({ length: p.count }).map((_, idx) => (
                  <div 
                    key={`l-${p.weight}-${idx}`} 
                    className="bg-primary/80 border border-primary text-[8px] font-mono text-primary-foreground flex items-center justify-center rounded-sm"
                    style={{ 
                      width: '12px', 
                      height: `${30 + p.weight * 0.8}px`,
                    }}
                    title={`${p.weight} ${unit}`}
                  >
                    <span className="rotate-90 origin-center scale-75">{p.weight}</span>
                  </div>
                ))
              )}
            </div>
            {/* Center Bar */}
            <div className="w-16 h-4 bg-zinc-700 border border-zinc-600 rounded-sm flex items-center justify-center text-[9px] font-mono text-zinc-300 font-bold shrink-0">
              BAR
            </div>
            {/* Right side plates */}
            <div className="flex items-center gap-0.5">
              {plates.map(p => 
                Array.from({ length: p.count }).map((_, idx) => (
                  <div 
                    key={`r-${p.weight}-${idx}`} 
                    className="bg-primary/80 border border-primary text-[8px] font-mono text-primary-foreground flex items-center justify-center rounded-sm"
                    style={{ 
                      width: '12px', 
                      height: `${30 + p.weight * 0.8}px`,
                    }}
                    title={`${p.weight} ${unit}`}
                  >
                    <span className="rotate-90 origin-center scale-75">{p.weight}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
